import React from 'react';
import type { Option } from './formOptions';

interface BaseProps {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
}

function Label({ id, label, required }: Pick<BaseProps, 'id' | 'label' | 'required'>) {
  return (
    <label htmlFor={id} className="sl-label">
      {label}
      {required ? <span className="sl-req" aria-hidden="true"> *</span> : <span className="sl-optional"> (optional)</span>}
    </label>
  );
}

function describedBy(id: string, hint?: string, error?: string) {
  return [hint && `${id}-hint`, error && `${id}-error`].filter(Boolean).join(' ') || undefined;
}

function Messages({ id, hint, error }: Pick<BaseProps, 'id' | 'hint' | 'error'>) {
  return (
    <>
      {hint && <p id={`${id}-hint`} className="sl-hint">{hint}</p>}
      {error && <p id={`${id}-error`} className="sl-error">{error}</p>}
    </>
  );
}

export function TextField({
  id, label, error, required, hint, value, onChange, type = 'text', autoComplete, maxLength, inputMode,
}: BaseProps & {
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'email' | 'url';
  autoComplete?: string;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
}) {
  return (
    <div className="sl-field">
      <Label id={id} label={label} required={required} />
      <input
        id={id}
        name={id}
        type={type}
        className="sl-input"
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(id, hint, error)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        inputMode={inputMode}
      />
      <Messages id={id} hint={hint} error={error} />
    </div>
  );
}

export function TextAreaField({
  id, label, error, required, hint, value, onChange, maxLength,
}: BaseProps & { value: string; onChange: (value: string) => void; maxLength: number }) {
  return (
    <div className="sl-field">
      <Label id={id} label={label} required={required} />
      <textarea
        id={id}
        name={id}
        className="sl-input sl-textarea"
        rows={4}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(id, hint, error)}
        maxLength={maxLength}
      />
      <Messages id={id} hint={hint} error={error} />
    </div>
  );
}

export function SelectField({
  id, label, error, required, hint, value, onChange, options, placeholder = 'Select…',
}: BaseProps & { value: string; onChange: (value: string) => void; options: Option[]; placeholder?: string }) {
  return (
    <div className="sl-field">
      <Label id={id} label={label} required={required} />
      <select
        id={id}
        name={id}
        className="sl-input sl-select"
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        aria-required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={describedBy(id, hint, error)}
      >
        <option value="">{placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <Messages id={id} hint={hint} error={error} />
    </div>
  );
}

export function RadioGroupField({
  id, label, error, required, hint, value, onChange, options,
}: BaseProps & { value: string; onChange: (value: string) => void; options: Option[] }) {
  return (
    <fieldset
      className="sl-field sl-fieldset"
      aria-invalid={Boolean(error)}
      aria-describedby={describedBy(id, hint, error)}
    >
      <legend className="sl-label">
        {label}
        {required && <span className="sl-req" aria-hidden="true"> *</span>}
      </legend>
      <div className="sl-radio-list">
        {options.map((o, i) => (
          <label key={o.value} className="sl-radio" data-checked={value === o.value}>
            <input
              // The first radio carries the group id so error summaries can focus it.
              id={i === 0 ? id : undefined}
              type="radio"
              name={id}
              value={o.value}
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              required={required}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
      <Messages id={id} hint={hint} error={error} />
    </fieldset>
  );
}

export function ConsentField({
  id, label, error, checked, onChange,
}: { id: string; label: string; error?: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="sl-field">
      <label className="sl-consent">
        <input
          id={id}
          name={id}
          type="checkbox"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          required
          aria-required
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span>{label} <span className="sl-req" aria-hidden="true">*</span></span>
      </label>
      {error && <p id={`${id}-error`} className="sl-error">{error}</p>}
    </div>
  );
}

/** Off-screen field bots tend to fill. Not `display:none` so naive bots still see it. */
export function HoneypotField({
  id, value, onChange,
}: { id: string; value: string; onChange: (value: string) => void }) {
  return (
    <div className="sl-hp" aria-hidden="true">
      <label htmlFor={id}>Leave this field empty</label>
      <input
        id={id}
        name={id}
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

export type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

const ERROR_MESSAGES: Record<string, (email: string) => React.ReactNode> = {
  validation: () => 'Please fix the highlighted fields and try again.',
  rate_limited: () => 'We’ve received a lot of submissions from your network. Please try again in an hour.',
  unavailable: email => (
    <>Submissions aren’t available right now. Please email us at <a href={`mailto:${email}`}>{email}</a> instead.</>
  ),
  error: email => (
    <>Something went wrong and your submission wasn’t saved. Please try again, or email <a href={`mailto:${email}`}>{email}</a>.</>
  ),
};

export function SubmitError({ reason, contactEmail }: { reason: string; contactEmail: string }) {
  const render = ERROR_MESSAGES[reason] ?? ERROR_MESSAGES.error;
  return (
    <div className="sl-alert sl-alert-error" role="alert">
      {render(contactEmail)}
    </div>
  );
}

/** Focus the first invalid control after a failed submit (field order = DOM order). */
export function focusFirstError(form: HTMLFormElement | null, errors: Record<string, string>) {
  if (!form) return;
  const ids = Object.keys(errors);
  const el = Array.from(form.querySelectorAll<HTMLElement>('[id]')).find(node => ids.includes(node.id));
  el?.focus();
}
